import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  inject,
  input,
  OnInit,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { debounceTime, distinctUntilChanged, Subject } from 'rxjs';
import { ActivatedRoute, Router } from '@angular/router';
import { DatePipe } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { FormsModule } from '@angular/forms';
import {
  CABINET_PATHS,
  formatOrderShortPublicId,
  getClientOrderPreferredScheduleAt,
  getClientOrderScheduleAt,
  getStaffOrderKindLabelKeyForRow,
} from '@nebalkon-workspace/cabinet-core';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import {
  OrderApiService,
  OrderListQuery,
  OrderResponse,
  OrderStatuses,
} from '@nebalkon-workspace/order-api';
import {
  STAFF_ORDER_KIND_FILTERS,
  STAFF_ORDER_STATUS_FILTERS,
  StaffOrderKindFilter,
  StaffOrderStatusFilter,
} from './staff-order-filters.const';
import {
  NotButton,
  NotInput,
  NotSystemNotificationService,
} from '@nebalkon-workspace/ui-kit';

export type { StaffOrderKindFilter, StaffOrderStatusFilter } from './staff-order-filters.const';

@Component({
  selector: 'lib-staff-orders-list',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    TranslateModule,
    FormsModule,
    NotButton,
    NotInput,
    DatePipe,
  ],
  templateUrl: './staff-orders-list.component.html',
  styleUrl: './staff-orders-list.component.scss',
})
export class StaffOrdersListComponent implements OnInit {
  readonly embeddedInCabinetPage = input(false);

  readonly #cabinetPaths = inject(CABINET_PATHS);
  readonly #api = inject(OrderApiService);
  readonly #route = inject(ActivatedRoute);
  readonly #router = inject(Router);
  readonly #translate = inject(TranslateService);
  readonly #notification = inject(NotSystemNotificationService);
  readonly #destroyRef = inject(DestroyRef);

  protected readonly kindFilters = STAFF_ORDER_KIND_FILTERS;
  protected readonly statusFilters = STAFF_ORDER_STATUS_FILTERS;

  protected readonly kindFilter = signal<StaffOrderKindFilter>('all');
  protected readonly orderStatusFilter = signal<StaffOrderStatusFilter>('all');
  protected readonly searchQuery = signal<string>('');
  private readonly searchSubject = new Subject<string>();

  protected readonly hasFiltersActive = computed(
    () =>
      this.kindFilter() !== 'all' ||
      this.orderStatusFilter() !== 'all' ||
      this.searchQuery().trim() !== '',
  );

  private readonly primaryLineMaxLen = 72;

  protected readonly items = signal<OrderResponse[]>([]);
  protected readonly loading = signal(true);
  protected readonly loadingMore = signal(false);
  protected readonly accessDenied = signal(false);
  protected readonly error = signal<string | null>(null);
  protected readonly hasMore = signal(false);
  protected readonly nextCursor = signal<string | null>(null);

  public ngOnInit(): void {

    this.#route.queryParamMap
      .pipe(takeUntilDestroyed(this.#destroyRef))
      .subscribe((qm) => {
        const uid = qm.get('user_id')?.trim() ?? '';
        if (uid) {
          this.searchQuery.set(uid);
        }
        this.#applyFiltersFromQuery(qm.get('type'), qm.get('status'));
        this.#loadFirstPage();
      });

    this.searchSubject
      .pipe(
        debounceTime(500),
        distinctUntilChanged(),
        takeUntilDestroyed(this.#destroyRef),
      )
      .subscribe(() => {
        const s = this.searchQuery().trim();
        const urlUid =
          this.#route.snapshot.queryParamMap.get('user_id')?.trim() ?? '';
        const matchesUserFilter =
          urlUid.length > 0 &&
          s.length > 0 &&
          s.toLowerCase() === urlUid.toLowerCase();
        if (urlUid && (s === '' || !matchesUserFilter)) {
          void this.#router.navigate([], {
            relativeTo: this.#route,
            queryParams: { user_id: null },
            queryParamsHandling: 'merge',
          });
          return;
        }
        this.#loadFirstPage();
      });
  }

  protected setKindFilter(id: StaffOrderKindFilter): void {
    if (this.kindFilter() === id) {
      return;
    }
    void this.#router.navigate([], {
      relativeTo: this.#route,
      queryParams: { type: id === 'all' ? null : id },
      queryParamsHandling: 'merge',
      replaceUrl: true,
    });
  }

  protected setOrderStatusFilter(id: StaffOrderStatusFilter): void {
    if (this.orderStatusFilter() === id) {
      return;
    }
    void this.#router.navigate([], {
      relativeTo: this.#route,
      queryParams: { status: id === 'all' ? null : id },
      queryParamsHandling: 'merge',
      replaceUrl: true,
    });
  }

  protected onSearchChange(value: string): void {
    this.searchQuery.set(value);
    this.searchSubject.next(value);
  }

  protected clearSearch(): void {
    this.searchQuery.set('');
    if (this.#route.snapshot.queryParamMap.get('user_id')?.trim()) {
      void this.#router.navigate([], {
        relativeTo: this.#route,
        queryParams: { user_id: null },
        queryParamsHandling: 'merge',
      });
    } else {
      this.#loadFirstPage();
    }
  }

  protected onOpen(id: string): void {
    void this.#router.navigate([this.#cabinetPaths.staff, 'orders', id]);
  }

  protected loadMore(): void {
    const cursor = this.nextCursor();
    if (!cursor || this.loadingMore()) {
      return;
    }
    this.loadingMore.set(true);
    const query = this.#buildQuery(cursor);
    this.#api.listOrders(query).subscribe({
      next: (res) => {
        this.items.update((prev) => [...prev, ...res.data]);
        this.nextCursor.set(res.next_cursor ?? null);
        this.hasMore.set(Boolean(res.next_cursor));
        this.loadingMore.set(false);
      },
      error: (err: HttpErrorResponse) => {
        this.loadingMore.set(false);
        this.#toastError(err);
      },
    });
  }

  protected statusLabelKey(status: string): string {
    const map: Record<string, string> = {
      [OrderStatuses.New]: 'admin.storageRequests.orderStatusNew',
      [OrderStatuses.InProcess]: 'admin.storageRequests.orderStatusInProcess',
      [OrderStatuses.Pending]: 'admin.storageRequests.orderStatusPending',
      [OrderStatuses.InWork]: 'admin.storageRequests.orderStatusInWork',
      [OrderStatuses.InTransit]: 'admin.storageRequests.orderStatusInTransit',
      [OrderStatuses.Stored]: 'admin.storageRequests.orderStatusStored',
      [OrderStatuses.Completed]: 'admin.storageRequests.filterOrderCompleted',
      [OrderStatuses.Canceled]: 'admin.storageRequests.filterOrderCancelled',
    };
    return map[status] ?? status;
  }

  protected readonly formatOrderShortPublicId = formatOrderShortPublicId;

  protected kindLabelKey(row: OrderResponse): string {
    return getStaffOrderKindLabelKeyForRow(row);
  }

  protected formatPhone(phone: string): string {
    if (!phone) {
      return '';
    }
    const cleaned = phone.replace(/\D/g, '');
    if (cleaned.startsWith('375')) {
      return `+${cleaned}`;
    }
    if (cleaned.length === 9) {
      return `+375${cleaned}`;
    }
    return `+${cleaned}`;
  }

  protected cardPrimaryLine(row: OrderResponse): string {
    const addr =
      row.pickup_address?.trim() || row.delivery_address?.trim() || '';
    if (addr) {
      return this.#truncateEllipsis(addr, this.primaryLineMaxLen);
    }
    return row.first_name?.trim() || this.formatPhone(row.phone);
  }

  protected cardContactLine(row: OrderResponse): string | null {
    const addr =
      row.pickup_address?.trim() || row.delivery_address?.trim() || '';
    if (!addr) {
      return null;
    }
    const name = row.first_name?.trim();
    const phone = this.formatPhone(row.phone);
    if (name && phone) {
      return `${name} · ${phone}`;
    }
    return name || phone || null;
  }

  protected agreedScheduleAt(row: OrderResponse): string | undefined {
    return getClientOrderScheduleAt(row);
  }

  protected preferredScheduleAt(row: OrderResponse): string | undefined {
    return getClientOrderPreferredScheduleAt(row);
  }

  protected showPreferredSchedulePending(row: OrderResponse): boolean {
    return !this.agreedScheduleAt(row) && Boolean(this.preferredScheduleAt(row));
  }

  #truncateEllipsis(text: string, maxLen: number): string {
    if (text.length <= maxLen) {
      return text;
    }
    return `${text.slice(0, Math.max(0, maxLen - 1)).trimEnd()}…`;
  }

  #applyFiltersFromQuery(type: string | null, status: string | null): void {
    const kind = this.#parseKindFilter(type);
    const orderStatus = this.#parseStatusFilter(status);
    this.kindFilter.set(kind);
    this.orderStatusFilter.set(orderStatus);
  }

  #parseKindFilter(value: string | null): StaffOrderKindFilter {
    if (
      value === 'pickup' ||
      value === 'delivery' ||
      value === 'self_dropoff' ||
      value === 'self_pickup'
    ) {
      return value;
    }
    return 'all';
  }

  #parseStatusFilter(value: string | null): StaffOrderStatusFilter {
    const allowed: StaffOrderStatusFilter[] = [
      'new',
      'in_process',
      'pending',
      'in_work',
      'in_transit',
      'stored',
      'completed',
      'canceled',
    ];
    if (value && allowed.includes(value as StaffOrderStatusFilter)) {
      return value as StaffOrderStatusFilter;
    }
    return 'all';
  }

  #buildQuery(cursor?: string): OrderListQuery {
    const q: OrderListQuery = { limit: 30 };
    const k = this.kindFilter();
    if (k !== 'all') {
      q.type = k;
    }
    const st = this.orderStatusFilter();
    if (st !== 'all') {
      q.status = st;
    }
    const s = this.searchQuery().trim();
    const uidFromUrl =
      this.#route.snapshot.queryParamMap.get('user_id')?.trim() ?? '';
    if (
      uidFromUrl &&
      s.length > 0 &&
      s.toLowerCase() === uidFromUrl.toLowerCase()
    ) {
      q.user_id = uidFromUrl;
    } else if (s) {
      q.search = s;
    }
    if (cursor) {
      q.cursor = cursor;
    }
    return q;
  }

  #loadFirstPage(): void {
    this.loading.set(true);
    this.error.set(null);
    this.accessDenied.set(false);
    this.#api.listOrders(this.#buildQuery()).subscribe({
      next: (res) => {
        this.items.set(res.data);
        this.nextCursor.set(res.next_cursor ?? null);
        this.hasMore.set(Boolean(res.next_cursor));
        this.loading.set(false);
      },
      error: (err: HttpErrorResponse) => {
        this.loading.set(false);
        if (err.status === 403) {
          this.accessDenied.set(true);
        } else {
          this.error.set(
            this.#translate.instant('admin.storageRequests.loadError'),
          );
        }
      },
    });
  }

  #toastError(err: HttpErrorResponse): void {
    const msg =
      err.status === 403
        ? this.#translate.instant('admin.storageRequests.accessDenied')
        : this.#translate.instant('admin.storageRequests.loadError');
    this.#notification.addToQueue({
      id: Date.now().toString(),
      type: 'error',
      text: [msg],
    });
  }
}
